// src/components/hooks/useCategoryCity.ts

import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

export interface EmpresaListado {
  id_empresa: string;
  empresa: string;
  logo: string | null;
  descripcion: string | null;
  telefono: string | null;
  reservas_online_citas: boolean;
  latitud: number | null;
  longitud: number | null;
  imagenes: string[];
  valoracion_media_empresa: number | null;
  total_valoraciones: number | null;
  categoria_slug: string;
  ciudad_slug: string;
  empresa_slug: string;
  categoria: string;
  ciudad: string;
}

interface UseCategoryCityReturn {
  empresas: EmpresaListado[];
  loading: boolean;
  error: string | null;
  categoriaLabel: string;
  ciudadLabel: string;
  total: number;
}

/* Convierte un slug en texto legible: "mi-ciudad" → "Mi Ciudad" */
const humanizeSlug = (slug: string) =>
  slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const useCategoryCity = (
  categoriaSlug: string,
  ciudadSlug: string
): UseCategoryCityReturn => {
  const [empresas, setEmpresas] = useState<EmpresaListado[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoriaLabel, setCategoriaLabel] = useState(
    () => humanizeSlug(categoriaSlug) // valor inicial legible, no cadena vacía
  );
  const [ciudadLabel, setCiudadLabel] = useState(() =>
    humanizeSlug(ciudadSlug)
  );

  useEffect(() => {
    if (!categoriaSlug || !ciudadSlug) return;

    const fetchEmpresas = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: rpcError } = await supabase.rpc(
          'get_empresas_cliente',
          { p_email: null }
        );

        if (rpcError) throw rpcError;

        const todas: EmpresaListado[] = data ?? [];

        const filtradas = todas.filter(
          (e) =>
            e.categoria_slug === categoriaSlug && e.ciudad_slug === ciudadSlug
        );

        setEmpresas(filtradas);

        // Preferimos los labels que vienen del backend; si no, humanizamos el slug
        setCategoriaLabel(
          filtradas[0]?.categoria ?? humanizeSlug(categoriaSlug)
        );
        setCiudadLabel(filtradas[0]?.ciudad ?? humanizeSlug(ciudadSlug));
      } catch (err: any) {
        Sentry.captureException(err, {
          extra: { categoriaSlug, ciudadSlug },
        }); // 👈
        setError(err.message || 'Error al cargar empresas');
      } finally {
        setLoading(false);
      }
    };

    fetchEmpresas();
  }, [categoriaSlug, ciudadSlug]);

  return {
    empresas,
    loading,
    error,
    categoriaLabel,
    ciudadLabel,
    total: empresas.length,
  };
};
